import React from 'react';
import '../../../styles/components/segmented-control.css';

export interface SegmentedControlOption<T extends string> {
  value: T;
  label: string;
}

interface SegmentedControlProps<T extends string> {
  options: SegmentedControlOption<T>[];
  value: T;
  onChange: (value: T) => void;
  disabled?: boolean;
  className?: string;
  size?: 'sm' | 'md';
  variant?: 'filled' | 'ghost' | 'neutral';
}

function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  disabled = false,
  className = '',
  size = 'md',
  variant = 'filled'
}: SegmentedControlProps<T>) {
  const selectedIndex = options.findIndex(opt => opt.value === value);
  const controlName = `segmented-control-${Math.random().toString(36).substr(2, 9)}`;

  const classes = [
    'segmented-control',
    `segmented-control--${size}`,
    `segmented-control--${variant}`,
    disabled && 'segmented-control--disabled',
    className
  ].filter(Boolean).join(' ');

  return (
    <div
      className={classes}
      role="radiogroup"
      aria-disabled={disabled}
      style={{
        '--segment-count': options.length,
        '--selected-index': selectedIndex
      } as React.CSSProperties}
    >
      {/* Sliding indicator */}
      <div className="segmented-control__indicator" />

      {/* Options */}
      {options.map((option) => (
        <label
          key={option.value}
          className={`segmented-control__option ${option.value === value ? 'segmented-control__option--selected' : ''}`}
        >
          <input
            type="radio"
            name={controlName}
            value={option.value}
            checked={option.value === value}
            onChange={() => !disabled && onChange(option.value)}
            disabled={disabled}
          />
          <span className="segmented-control__label">{option.label}</span>
        </label>
      ))}
    </div>
  );
}

export default SegmentedControl;
